import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useSession } from "./SessionProvider";

// AssistantPanelProvider — workspace-shell-level state for the AI panel.
// Owns open/close + width. Persists per-user in localStorage so refresh
// keeps the panel where the user left it.
//
// Width clamps to [MIN_WIDTH, MAX_WIDTH]; default 360px. Width is the
// pixel value the drag-handle resize writes; the panel SCSS reads
// `--assistant-panel-width` from a CSS custom property on `.app-shell`.

const DEFAULT_WIDTH = 360;
export const MIN_WIDTH = 280;
export const MAX_WIDTH = 720;

// Record-scoped presentations (Enh 05): the panel re-fits between a floating
// chat box, a focused right drawer, a docked side column, and a minimized
// launcher bubble. Float is the default open mode.
export type AssistantMode = "float" | "drawer" | "dock" | "min";
const DEFAULT_MODE: AssistantMode = "float";
const VALID_MODES: AssistantMode[] = ["float", "drawer", "dock", "min"];

const STORAGE_KEY_OPEN = (uid: string) => `ede.assistant.open.${uid}`;
const STORAGE_KEY_WIDTH = (uid: string) => `ede.assistant.width.${uid}`;
const STORAGE_KEY_MODE = (uid: string) => `ede.assistant.mode.${uid}`;

function readMode(key: string, fallback: AssistantMode): AssistantMode {
    try {
        const v = localStorage.getItem(key) as AssistantMode | null;
        return v && VALID_MODES.includes(v) ? v : fallback;
    } catch {
        return fallback;
    }
}

function readBool(key: string, fallback: boolean): boolean {
    try {
        const v = localStorage.getItem(key);
        if (v === "1") return true;
        if (v === "0") return false;
        return fallback;
    } catch {
        return fallback;
    }
}

function readWidth(key: string, fallback: number): number {
    try {
        const v = localStorage.getItem(key);
        if (!v) return fallback;
        const n = parseInt(v, 10);
        if (Number.isNaN(n)) return fallback;
        return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, n));
    } catch {
        return fallback;
    }
}

export interface AssistantPanelContextValue {
    open: boolean;
    /** Active presentation when open. "min" shows only the launcher bubble. */
    mode: AssistantMode;
    width: number;
    toggle: () => void;
    setOpen: (next: boolean) => void;
    /** Set the presentation; passing "closed" fully closes the panel. */
    setMode: (next: AssistantMode | "closed") => void;
    setWidth: (w: number) => void;
    resetWidth: () => void;
}

const Ctx = createContext<AssistantPanelContextValue | null>(null);

export function AssistantPanelProvider({ children }: { children: ReactNode }) {
    const session = useSession();
    const userId = session.userId ?? "anonymous";

    const [open, setOpenState] = useState<boolean>(() => readBool(STORAGE_KEY_OPEN(userId), false));
    const [mode, setModeState] = useState<AssistantMode>(() => readMode(STORAGE_KEY_MODE(userId), DEFAULT_MODE));
    const [width, setWidthState] = useState<number>(() => readWidth(STORAGE_KEY_WIDTH(userId), DEFAULT_WIDTH));

    // Persist open + width whenever they change. Wrapped in try/catch so
    // a quota-exceeded / disabled-storage browser doesn't crash the app.
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_OPEN(userId), open ? "1" : "0");
        } catch {
            // ignore
        }
    }, [open, userId]);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_WIDTH(userId), String(width));
        } catch {
            // ignore
        }
    }, [width, userId]);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_MODE(userId), mode);
        } catch {
            // ignore
        }
    }, [mode, userId]);

    const setOpen = useCallback((next: boolean) => {
        setOpenState(next);
        // Re-opening from a minimized state should show a real panel, not the bubble.
        if (next) setModeState((m) => (m === "min" ? DEFAULT_MODE : m));
    }, []);
    const toggle = useCallback(() => {
        setOpenState((prev) => {
            if (!prev) setModeState((m) => (m === "min" ? DEFAULT_MODE : m));
            return !prev;
        });
    }, []);
    // "closed" fully closes; any presentation (incl. "min") opens the panel.
    const setMode = useCallback((next: AssistantMode | "closed") => {
        if (next === "closed") {
            setOpenState(false);
            return;
        }
        setModeState(next);
        setOpenState(true);
    }, []);
    const setWidth = useCallback((w: number) => {
        setWidthState(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(w))));
    }, []);
    const resetWidth = useCallback(() => setWidthState(DEFAULT_WIDTH), []);

    const value = useMemo<AssistantPanelContextValue>(
        () => ({ open, mode, width, toggle, setOpen, setMode, setWidth, resetWidth }),
        [open, mode, width, toggle, setOpen, setMode, setWidth, resetWidth],
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAssistantPanel(): AssistantPanelContextValue {
    const ctx = useContext(Ctx);
    if (ctx === null) {
        throw new Error("useAssistantPanel: must be inside <AssistantPanelProvider>");
    }
    return ctx;
}
